import { Injectable } from '@angular/core';
import {
  ClickCalculationResult,
  ClickCalculatorDimensionInput,
  ClickCalculatorCellHealth,
  ClickCalculatorDimensionResult,
  ClickCalculatorGroupResult,
  ClickCalculatorInput,
  ClickCalculatorPace,
  ClickCalculatorProjectionBreakdown,
  ClickCalculatorQuotaHealth,
  ClickCalculatorSeverity,
  ClickCalculatorTrajectorySegment,
} from '../models/click-calculation';

/** Guards the cascade against floating-point residue when a group closes. */
const CLOSURE_EPSILON = 0.000001;

/**
 * Share of remaining click capacity a group may need before it bands as `warn`. Above 1.0 it
 * cannot close in time and bands as `crit`.
 */
const SEVERITY_HEADROOM = 0.75;

/**
 * A quota table whose goals sum above this multiple of the Nav project goal is treated as a
 * counter, not a limit, and dropped. The 5% allows for programmer rounding.
 */
const QUOTA_GOAL_TOLERANCE = 1.05;

interface CascadeEntry {
  group: ClickCalculatorGroupResult;
  remaining: number;
}

/**
 * OR's Net IR methodology at quota-group level. Kept free of HTTP and view concerns so it can be
 * hosted on other screens.
 */
@Injectable({ providedIn: 'root' })
export class ClickCalculatorService {

  /** Null rather than zero when there is no denominator: unmeasured is not the same as zero. */
  private ratio(numerator: number, denominator: number): number | null {
    return denominator > 0 ? numerator / denominator : null;
  }

  /** A rate held at or under 100%. Null passes through. */
  private capRate(rate: number | null): number | null {
    return rate === null ? null : Math.min(rate, 1);
  }

  calculate(input: ClickCalculatorInput): ClickCalculationResult {
    // Needed up front: severity bands are measured against remaining capacity.
    const { deliverableClicks } = this.capacity(input);
    // Shared with the pace verdict so the lights and the badge withhold on the same day.
    const rated = this.isRated(input);

    // Dimensions with no hard-limit cells are reporting breaks, not quotas — drop them.
    const withLimits = input.dimensions
      .map(dimension =>
        this.calculateDimension(
          dimension,
          input.projectTerms,
          input.projectClicks,
          deliverableClicks,
          rated
        )
      )
      .filter(dimension => dimension.groups.length > 0);

    // Dimensions whose goals sum past the Nav goal are counters: drop and report them. With no
    // project goal there is nothing to compare against, so all are kept.
    const goalCeiling = input.projectGoal * QUOTA_GOAL_TOLERANCE;
    const overGoal = (dimension: ClickCalculatorDimensionResult): boolean =>
      input.projectGoal > 0
        && dimension.groups.reduce((sum, group) => sum + group.goal, 0) > goalCeiling;
    const dimensions = withLimits.filter(dimension => !overGoal(dimension));
    const overGoalDimensionIds = withLimits.filter(overGoal).map(dimension => dimension.id);

    // A hard-limit group with no conversions has no rate, so `max(Goal ÷ IR)` would skip it and
    // understate the job. Such groups withhold the projection instead.
    //
    // Exempt:
    // - Goal 0: Decipher uses a 0 limit to switch a segment off (e.g. other countries on a shared
    //   survey). It needs nothing, so can never set the total.
    // - Targetable groups: they can be bought directly. They're listed separately in
    //   `skippedTargetableGroups` so the projection can carry a caveat.
    const unprojectableGroups = dimensions
      .flatMap(dimension => dimension.groups)
      .filter(group => !group.projectable && group.goal > 0 && !group.targetable);

    const skippedTargetableGroups = dimensions
      .flatMap(dimension => dimension.groups)
      .filter(group => !group.projectable && group.goal > 0 && group.targetable);

    // With no hard quotas, fall back to projecting the project goal at the observed conversion
    // rate — cruder than the cascade, but better than nothing.
    const hasQuotas = dimensions.length > 0;
    // Clicks in the denominator, so the result is already in clicks with dropout included.
    const observedConversion =
      input.projectClicks > 0 ? input.projectCompletes / input.projectClicks : 0;

    const insufficientData = hasQuotas
      ? unprojectableGroups.length > 0
        || dimensions.every(dimension => dimension.totalClicks === 0)
      : observedConversion <= 0;

    // The Nav goal at the observed conversion rate is the floor: the job isn't done until the
    // Nav goal is, whatever survey quotas say. The quota cascade can raise this, never lower it.
    const goalClicks = observedConversion > 0 ? input.projectGoal / observedConversion : 0;
    const goalClicksStillNeeded = observedConversion > 0
      ? Math.max(input.projectGoal - input.projectCompletes, 0) / observedConversion
      : 0;

    // Max across dimensions, not the sum: they partition the same respondents.
    const cascadeClicks = hasQuotas
      ? Math.max(...dimensions.map(dimension => dimension.totalClicks))
      : goalClicks;
    const projectedClicks = Math.max(cascadeClicks, goalClicks);
    // True when the Nav goal, not a quota cell, sets the figure, so no cell is named as driver.
    const goalSetsTotal = hasQuotas && goalClicks > cascadeClicks;
    // The binding quota only describes the job at close when it sets the total. When the Nav goal
    // does, the quota covers a slice of the job, so the at-close rates fall back to today's.
    const quotaSetsClose = hasQuotas && !goalSetsTotal;
    // Pick the dimension first so the figure and its named group always match.
    const stillNeededDimension = dimensions.length
      ? dimensions.reduce((worst, dimension) =>
          dimension.clicksStillNeeded > worst.clicksStillNeeded ? dimension : worst
        )
      : null;
    const cascadeStillNeeded = hasQuotas
      ? stillNeededDimension?.clicksStillNeeded ?? 0
      : goalClicksStillNeeded;
    const clicksStillNeeded = Math.max(cascadeStillNeeded, goalClicksStillNeeded);
    const goalSetsStillNeeded = hasQuotas && goalClicksStillNeeded > cascadeStillNeeded;

    // The dimension needing the most clicks is binding; the others close inside it.
    const binding = dimensions.length
      ? dimensions.reduce((worst, dimension) =>
          dimension.totalClicks > worst.totalClicks ? dimension : worst
        )
      : null;
    if (binding) {
      binding.isBinding = true;
    }

    // Terms at close = screener starts − completes − overquotas. Sized off screener starts, not
    // clicks, so QC removals, drop-offs and dupes aren't counted as screen-outs.
    const bindingGoal = binding
      ? binding.groups.reduce((sum, group) => sum + group.goal, 0)
      : 0;
    const bindingScreenerStarts = binding ? binding.screenerStarts : 0;
    const projectedOverQuotas = binding
      ? binding.groups.reduce((sum, group) => sum + group.projectedOverQuotas, 0)
      : 0;
    const projectedTerms = Math.max(
      Math.round(bindingScreenerStarts - bindingGoal - projectedOverQuotas),
      0
    );

    // Non-targetable share of each projected loss, so True IR at close uses the same denominator
    // as today's figure. Every overquota here is on the binding dimension's quota, so its
    // targetability decides whether they count.
    const projectedNonTargetableOverQuotas =
      binding && !binding.targetable ? projectedOverQuotas : 0;

    // Terms carry no dimension, so today's non-targetable share (from True IR) is
    // carried forward. No terms today is treated as no split.
    const nonTargetableTermShare =
      input.projectTerms > 0 ? (input.nonTargetableTerms ?? 0) / input.projectTerms : 0;
    const projectedNonTargetableTerms = Math.max(
      Math.round(projectedTerms * nonTargetableTermShare),
      0
    );

    // Nav definitions: Net IR counts every term; True IR counts only non-targetable losses.
    const netIrDenominator = input.projectCompletes + input.projectTerms;
    // Withheld, not zeroed, while True IR isn't calculated — zeroes would print a false 100%.
    const trueIrUncalculated =
      input.nonTargetableTerms === null || input.nonTargetableOverQuotas === null;
    const trueIrDenominator = trueIrUncalculated
      ? 0
      : input.projectCompletes +
        (input.nonTargetableTerms ?? 0) +
        (input.nonTargetableOverQuotas ?? 0);

    const clicksAtBid =
      input.expectedIrPercent !== null && input.expectedIrPercent > 0
        ? input.projectGoal / (input.expectedIrPercent / 100)
        : null;

    // Withholds every projected figure while nothing is projectable; otherwise terms come out at
    // 0 and Net IR at close reads a false 100%.
    const projected = <T>(value: T): T | null => (insufficientData ? null : value);

    const difficultyMultiple =
      clicksAtBid !== null && clicksAtBid > 0 && projectedClicks > 0
        ? projectedClicks / clicksAtBid
        : null;
    const threatGroup = this.findThreatGroup(dimensions);

    const netIr = this.ratio(input.projectCompletes, netIrDenominator);
    const trueIr = this.ratio(input.projectCompletes, trueIrDenominator);

    const epc = input.projectSpend === null ? null : this.ratio(input.projectSpend, input.projectClicks);
    const roundedStillNeeded = Math.round(clicksStillNeeded);

    return {
      netIr,
      trueIr,
      trueIrUncalculated,
      conversionRate: this.ratio(input.projectCompletes, input.projectClicks),
      // Capped at 100%; anything higher indicates an upstream data defect.
      projectedConversionRate: projected(this.capRate(this.ratio(input.projectGoal, projectedClicks))),
      // Without a quota that sets the close, the at-close rates equal today's.
      netIrAtClose: quotaSetsClose
        ? projected(this.ratio(bindingGoal, bindingGoal + projectedTerms))
        : projected(netIr),
      // Same definition as today's True IR: completes over completes + non-targetable terms and
      // overquotas. Withheld whenever today's is, since there'd be no baseline to compare.
      trueIrAtClose: trueIrUncalculated
        ? null
        : quotaSetsClose
          ? projected(this.ratio(
              bindingGoal,
              bindingGoal + projectedNonTargetableTerms + projectedNonTargetableOverQuotas
            ))
          : projected(trueIr),
      currentClicks: input.projectClicks,
      projectedClicks: projected(Math.round(projectedClicks)),
      clicksStillNeeded: projected(Math.round(clicksStillNeeded)),
      clicksStillNeededGroup: projected(
        goalSetsStillNeeded ? null : stillNeededDimension?.stillNeededGroup ?? null
      ),
      projectedClicksGroup: projected(goalSetsTotal ? null : binding?.closingGroup ?? null),
      unprojectableGroups,
      skippedTargetableGroups,
      pace: this.buildPace(input, clicksStillNeeded, insufficientData),
      projectGoal: input.projectGoal,
      currentCompletes: input.projectCompletes,
      goalReached: input.projectGoal > 0 && input.projectCompletes >= input.projectGoal,
      completesShort: Math.max(input.projectGoal - input.projectCompletes, 0),
      clicksAtBid: clicksAtBid === null ? null : Math.round(clicksAtBid),
      epc,
      costToFinish: epc === null || insufficientData ? null : roundedStillNeeded * epc,
      projectionBreakdown: projected(
        this.buildProjectionBreakdown(input, netIr, projectedClicks, clicksAtBid)
      ),
      expectedIrPercent: input.expectedIrPercent,
      difficultyMultiple,
      dimensions,
      overGoalDimensionIds,
      threatGroup,
      quotaHealth: this.buildQuotaHealth(dimensions, input, rated),
      insufficientData,
    };
  }

  /**
   * Every open cell (targetable or not) read against the target close date and ranked. Before
   * the end date it's a forecast; once lapsed it's a scorecard of shortfalls.
   */
  private buildQuotaHealth(
    dimensions: ClickCalculatorDimensionResult[],
    input: ClickCalculatorInput,
    rated: boolean
  ): ClickCalculatorQuotaHealth {
    const { clicksPerDay } = this.capacity(input);
    // Signed: a project past its end date is still ranked against that date.
    const daysToTarget = input.daysLeft;
    // Zero counts as lapsed: whole-day counting leaves no capacity on the last day.
    const lapsed = daysToTarget <= 0;

    const cells = dimensions
      .flatMap(dimension => dimension.groups)
      .filter(group => group.need > 0);

    // Cells with no completes can't be placed on the day scale; they're listed separately rather
    // than withholding the whole section.
    const projectable = cells.filter(group => group.clicksToClose !== null);
    const unprojectable = cells.filter(group => group.clicksToClose === null);

    // Filled cells only matter on the scorecard.
    const closedCells = lapsed
      ? dimensions.flatMap(dimension => dimension.groups).filter(group => group.need <= 0)
      : [];

    // Ordered most fundamental first, so the reason given is the one to fix first. `no-cells` is
    // a finding and must only be reached once quota data was actually read. Once lapsed, the
    // scorecard needs no rate, so the rate gates don't apply.
    const withheldReason = lapsed
      ? input.dimensions.length === 0
        ? 'no-quota-data' as const
        : dimensions.length === 0
          ? 'no-hard-limits' as const
          : cells.length === 0 && closedCells.length === 0
            ? 'no-cells' as const
            : null
      : !rated
        ? 'unrated' as const
        : clicksPerDay <= 0
          ? 'no-rate' as const
          : input.dimensions.length === 0
            ? 'no-quota-data' as const
            : dimensions.length === 0
              ? 'no-hard-limits' as const
              : cells.length === 0
                ? 'no-cells' as const
                : null;

    if (withheldReason !== null) {
      return {
        available: false,
        withheldReason,
        daysToTarget,
        lapsed,
        clicksPerDay,
        cellCount: projectable.length,
        atRisk: [],
        onTrack: [],
        closed: [],
        watch: null,
        unprojectable: [],
        scaleDays: daysToTarget,
      };
    }

    const rates = projectable.map(group => group.conversionRate);
    const lowestRate = Math.min(...rates);
    const highestRate = Math.max(...rates);
    // Lowest/highest notes are meaningless with fewer than three cells or when all rates match.
    const rateNotesMeaningful = projectable.length >= 3 && lowestRate !== highestRate;

    const place = (group: ClickCalculatorGroupResult): ClickCalculatorCellHealth => {
      // Null clicksToClose / zero rate only occur on the scorecard, which doesn't use daysToClose.
      const clicksToClose = group.clicksToClose;
      const daysToClose =
        clicksToClose !== null && clicksPerDay > 0 ? clicksToClose / clicksPerDay : 0;
      const daysSpare = daysToTarget - daysToClose;

      return {
        id: group.id,
        label: group.label,
        dimensionLabel: group.dimensionLabel,
        conversionRate: group.conversionRate,
        goal: group.goal,
        have: group.have,
        short: group.need,
        fillShare: group.goal > 0 ? Math.min(group.have / group.goal, 1) : 0,
        clicksToClose: clicksToClose === null ? null : Math.round(clicksToClose),
        daysToClose,
        daysSpare,
        // Capped at the cell's own requirement; past the end date the uncapped figure would also
        // count elapsed days.
        overrunClicks: daysSpare < 0 && clicksToClose !== null
          ? Math.min(Math.round(-daysSpare * clicksPerDay), Math.round(clicksToClose))
          : 0,
        atRisk: daysSpare < 0,
        lowestRate: rateNotesMeaningful && group.conversionRate === lowestRate,
        highestRate: rateNotesMeaningful && group.conversionRate === highestRate,
      };
    };

    // The scorecard includes cells with no completes: they missed by their whole goal.
    const health: ClickCalculatorCellHealth[] = (lapsed ? cells : projectable).map(place);

    // Both panels lead with the cell closest to setting the close date. Once lapsed, rank by
    // shortfall instead.
    const atRisk = lapsed
      ? [...health].sort((a, b) => b.short - a.short)
      : health.filter(cell => cell.atRisk).sort((a, b) => a.daysSpare - b.daysSpare);
    const onTrack = lapsed
      ? []
      : health.filter(cell => !cell.atRisk).sort((a, b) => a.daysSpare - b.daysSpare);
    // Only lifted out when other on-track cells remain; a lone healthy cell stays on-track.
    const watch = onTrack.length > 1 ? onTrack[0] : null;

    // Kept in read order: closed cells all met the date, so there's nothing to rank.
    const closed = closedCells.map(place);

    return {
      available: true,
      withheldReason: null,
      daysToTarget,
      lapsed,
      clicksPerDay,
      cellCount: health.length + closed.length,
      atRisk,
      // `watch` is shown separately, so don't count it twice.
      onTrack: watch ? onTrack.slice(1) : onTrack,
      closed,
      watch,
      unprojectable: lapsed ? [] : unprojectable,
      // Always include the target line on the scale so on-track bars have a reference.
      scaleDays: Math.max(daysToTarget, ...health.map(cell => cell.daysToClose), 0),
    };
  }

  private calculateDimension(
    dimension: ClickCalculatorDimensionInput,
    projectTerms: number,
    currentClicks: number,
    deliverableClicks: number,
    rated: boolean
  ): ClickCalculatorDimensionResult {
    // Every rate divides by the project's Nav clicks, so cell rates are directly comparable with
    // the project conversion rate and need no gross-up for dropout.
    const groups: ClickCalculatorGroupResult[] = dimension.groups
      .filter(group => group.hasLimit)
      .map(group => {
        // Completes only; overquotas are projected separately below.
        const conversionRate = currentClicks > 0 ? group.have / currentClicks : 0;
        const need = Math.max(group.goal - group.have, 0);
        // Clicks are whole respondents — never show a fraction of one.
        const clicksToClose = conversionRate > 0 ? Math.round(need / conversionRate) : null;
        return {
          id: group.id,
          label: group.label,
          dimensionLabel: dimension.label,
          goal: group.goal,
          have: group.have,
          need,
          targetable: dimension.targetable,
          conversionRate,
          overQuotas: group.overQuotas,
          projectable: conversionRate > 0,
          unreached: group.unreached,
          clicksToClose,
          projectedOverQuotas: 0,
          closesAtStep: null,
          severity: this.severityFor(clicksToClose, deliverableClicks, rated),
        };
      });

    const limited = dimension.groups.filter(group => group.hasLimit);
    const projectable = groups.filter(group => group.projectable);

    // Exact, not approximate: the last group to close has the largest Goal ÷ rate.
    const totalClicks = projectable.length
      ? Math.max(...projectable.map(group => group.goal / group.conversionRate))
      : 0;
    // Kept as group objects, not just figures: matching on rounded totals later could tie.
    const closingGroup = projectable.length
      ? projectable.reduce((last, group) =>
          group.goal / group.conversionRate > last.goal / last.conversionRate ? group : last
        )
      : null;
    const stillNeededGroup = projectable.length
      ? projectable.reduce((worst, group) =>
          group.need / group.conversionRate > worst.need / worst.conversionRate ? group : worst
        )
      : null;
    const clicksStillNeeded = stillNeededGroup
      ? stillNeededGroup.need / stillNeededGroup.conversionRate
      : 0;

    // Projected off the qualification rate (completes + overquotas), since an overquota is a
    // respondent who qualified after the cell filled.
    groups.forEach(group => {
      const qualifyRate =
        currentClicks > 0 ? (group.have + group.overQuotas) / currentClicks : 0;
      group.projectedOverQuotas = group.projectable
        ? Math.max(Math.round(totalClicks * qualifyRate - group.goal), 0)
        : 0;
    });

    // Respondents reaching a screener verdict at close, for the incidence card. Share capped at 1
    // because pre-QC Decipher completes can briefly exceed Nav's click count.
    const verdicts =
      projectTerms +
      dimension.groups.reduce((sum, group) => sum + group.have + group.overQuotas, 0);
    const verdictShare = currentClicks > 0 ? Math.min(verdicts / currentClicks, 1) : 0;
    const screenerStarts = totalClicks * verdictShare;

    const trajectory = this.buildTrajectory(groups, totalClicks);
    const threats = this.threatCandidates(groups);

    return {
      id: dimension.id,
      label: dimension.label,
      targetable: dimension.targetable,
      targetingOverridden: dimension.targetingOverridden,
      targetingSavable: dimension.targetingSavable,
      targetingSetBy: dimension.targetingSetBy,
      targetingSetAt: dimension.targetingSetAt,
      unattributed: dimension.unattributed,
      groups: this.hardestFirst(groups),
      screenerStarts: Math.round(screenerStarts),
      totalClicks: Math.round(totalClicks),
      clicksStillNeeded: Math.round(clicksStillNeeded),
      stillNeededGroup,
      closingGroup,
      trajectory,
      isBinding: false,
      severity: this.worstSeverity(this.bandingCandidates(groups)),
      threatGroup: this.worstThreat(groups),
    };
  }

  /**
   * Bands on feasibility, not incidence: remaining clicks needed against clicks the schedule can
   * still deliver. Each group is measured against the *whole* capacity, since every click is
   * offered to all quotas at once. A group with no completes bands as `crit` — no evidence of
   * conversion is the worst case.
   */
  private severityFor(
    clicksToClose: number | null,
    deliverableClicks: number,
    rated: boolean
  ): ClickCalculatorSeverity {
    // Checked first: without a rated day, capacity means nothing.
    if (!rated) return 'unrated';
    if (clicksToClose === null) return 'crit';
    // A closed group asks for nothing, so it is feasible however little capacity is left.
    if (clicksToClose <= 0) return 'good';
    // No capacity left, so anything outstanding cannot close.
    if (deliverableClicks <= 0) return 'crit';

    const share = clicksToClose / deliverableClicks;
    if (share <= SEVERITY_HEADROOM) return 'good';
    if (share <= 1) return 'warn';
    return 'crit';
  }

  /**
   * Whether a full day in field exists to rate from. Single definition shared by the pace verdict
   * and the severity bands so they can't disagree.
   */
  private isRated(input: ClickCalculatorInput): boolean {
    return input.observedClicksPerDay > 0;
  }

  /**
   * Observed click rate and the clicks it can still deliver before field close. Shared by pace
   * and severity so remaining capacity has one definition.
   */
  private capacity(input: ClickCalculatorInput): {
    clicksPerDay: number;
    deliverableClicks: number;
  } {
    // No rate yet reads as zero here; `rated` carries the distinction.
    const clicksPerDay = Math.max(input.observedClicksPerDay, 0);

    return { clicksPerDay, deliverableClicks: clicksPerDay * Math.max(input.daysLeft, 0) };
  }

  /**
   * Walks the closure cascade for the trajectory display. Runs off Goal rather than Need because
   * it shows what the whole job costs, for the versus-bid comparison.
   */
  private buildTrajectory(
    groups: ClickCalculatorGroupResult[],
    totalClicks: number
  ): ClickCalculatorTrajectorySegment[] {
    const open: CascadeEntry[] = groups
      .filter(group => group.projectable)
      .map(group => ({ group, remaining: group.goal }));

    const segments: ClickCalculatorTrajectorySegment[] = [];
    let step = 1;
    let consumed = 0;

    while (open.length > 0) {
      const blendedRate = open.reduce((sum, entry) => sum + entry.group.conversionRate, 0);

      let closing = open[0];
      let roundClicks = closing.remaining / closing.group.conversionRate;
      open.forEach(entry => {
        const clicks = entry.remaining / entry.group.conversionRate;
        if (clicks < roundClicks) {
          roundClicks = clicks;
          closing = entry;
        }
      });

      segments.push({
        // Left unrounded here; rounding happens once, on the way out.
        clicks: roundClicks,
        conversionRate: blendedRate,
        closingGroupLabel: closing.group.label,
        share: totalClicks > 0 ? roundClicks / totalClicks : 0,
        // The closing group's band, not the blended rate's: the blend always reads greener than
        // its members, and red here means a threat, not difficulty.
        severity: closing.group.severity,
      });

      // A closed group keeps converting respondents; they land as overquotas from here.
      open.forEach(entry => {
        entry.remaining -= roundClicks * entry.group.conversionRate;
      });
      consumed += roundClicks;

      for (let index = open.length - 1; index >= 0; index--) {
        if (open[index] === closing || open[index].remaining <= CLOSURE_EPSILON) {
          open[index].group.closesAtStep = step;
          open.splice(index, 1);
        }
      }
      step++;
    }

    return segments.map(segment => ({
      ...segment,
      clicks: Math.round(segment.clicks),
    }));
  }

  /** Unprojectable groups first, then by clicks to close descending (last to close first). */
  private hardestFirst(groups: ClickCalculatorGroupResult[]): ClickCalculatorGroupResult[] {
    return [...groups].sort((a, b) => {
      if (a.projectable !== b.projectable) return a.projectable ? 1 : -1;
      return (b.clicksToClose ?? 0) - (a.clicksToClose ?? 0);
    });
  }

  /**
   * A threat is a non-targetable group that hasn't closed. Single definition so the dimension
   * light and the project threat card agree.
   */
  private threatCandidates(
    groups: ClickCalculatorGroupResult[]
  ): ClickCalculatorGroupResult[] {
    return groups.filter(group => !group.targetable && group.need > 0);
  }

  /**
   * Groups that decide a dimension's light: every outstanding group, targetable or not. Unlike
   * `threatCandidates`, a targetable group that can't close lights its own panel without
   * entering the project's threat ranking.
   */
  private bandingCandidates(
    groups: ClickCalculatorGroupResult[]
  ): ClickCalculatorGroupResult[] {
    return groups.filter(group => group.need > 0);
  }

  private worstThreat(
    groups: ClickCalculatorGroupResult[]
  ): ClickCalculatorGroupResult | null {
    const candidates = this.threatCandidates(groups);
    if (candidates.length === 0) return null;

    // Prefer the biggest quantified threat, since it can be sized and acted on. Groups with no
    // completes still band `crit` and block the projection via `unprojectableGroups`.
    const projectable = candidates.filter(group => group.projectable);
    if (projectable.length > 0) {
      return [...projectable].sort((a, b) => (b.clicksToClose ?? 0) - (a.clicksToClose ?? 0))[0];
    }

    return [...candidates].sort((a, b) => b.need - a.need)[0];
  }

  /**
   * Worst band present. `unrated` comes from project time in field, so it applies to all groups
   * or none.
   */
  private worstSeverity(groups: ClickCalculatorGroupResult[]): ClickCalculatorSeverity {
    if (groups.some(group => group.severity === 'unrated')) return 'unrated';
    if (groups.some(group => group.severity === 'crit')) return 'crit';
    if (groups.some(group => group.severity === 'warn')) return 'warn';
    return 'good';
  }

  private findThreatGroup(
    dimensions: ClickCalculatorDimensionResult[]
  ): ClickCalculatorGroupResult | null {
    return this.worstThreat(dimensions.flatMap(dimension => dimension.groups));
  }

  /**
   * Remaining click requirement against what the remaining days can supply at the observed rate.
   * Independent of the bid comparison.
   */
  private buildPace(
    input: ClickCalculatorInput,
    clicksStillNeeded: number,
    insufficientData: boolean
  ): ClickCalculatorPace {
    const daysLeft = Math.max(input.daysLeft, 0);
    const { clicksPerDay, deliverableClicks } = this.capacity(input);
    const rated = this.isRated(input);

    return {
      clicksPerDay,
      rateDays: input.observedRateDays,
      // Null with no days left: the requirement is unanswerable by rate, not infinite.
      requiredClicksPerDay: daysLeft > 0 ? clicksStillNeeded / daysLeft : null,
      daysLeft,
      deliverableClicks: Math.round(deliverableClicks),
      shortfall: Math.round(clicksStillNeeded - deliverableClicks),
      onTrack: rated ? deliverableClicks >= clicksStillNeeded : null,
      rated,
      // Independent of `rated`: a project with traffic has a requirement worth stating before its
      // first full day.
      known: !insufficientData && input.projectClicks > 0,
      daysToFinish: rated && !insufficientData && clicksPerDay > 0 && clicksStillNeeded > 0
        ? clicksStillNeeded / clicksPerDay
        : null,
    };
  }

  /**
   * Splits the bid-vs-projection gap into dropout the bid never modelled and incidence variance.
   * The middle term (goal at the bid's IR with the observed dropout) makes the two parts
   * like-for-like so they sum to the whole gap.
   *
   * Returns null on missing or degenerate inputs, including a screener reach above 1 (the
   * Decipher-vs-Nav count skew).
   */
  private buildProjectionBreakdown(
    input: ClickCalculatorInput,
    netIr: number | null,
    projectedClicks: number,
    clicksAtBid: number | null
  ): ClickCalculatorProjectionBreakdown | null {
    const conversionRate = this.ratio(input.projectCompletes, input.projectClicks);
    const expectedIr =
      input.expectedIrPercent === null ? null : input.expectedIrPercent / 100;

    if (
      netIr === null || netIr <= 0 ||
      conversionRate === null || conversionRate <= 0 ||
      expectedIr === null || expectedIr <= 0 ||
      clicksAtBid === null || input.projectGoal <= 0
    ) {
      return null;
    }

    const screenerReach = conversionRate / netIr;
    if (screenerReach <= 0 || screenerReach > 1) {
      return null;
    }

    // Round the three endpoints and take differences, so the two parts sum exactly to the gap the
    // screen prints from the same rounded figures.
    const bidClicks = Math.round(clicksAtBid);
    const totalClicks = Math.round(projectedClicks);
    const atExpectedIr = Math.round(input.projectGoal / (expectedIr * screenerReach));

    return {
      screenerReach,
      clicksAtExpectedIr: atExpectedIr,
      dropoutClicks: atExpectedIr - bidClicks,
      incidenceClicks: totalClicks - atExpectedIr,
    };
  }

}
